import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
import { NameData } from '../types';
import { loadNameData } from '../utils/name';

interface NamesListProps {
  gender: 'boys' | 'girls' | null;
  letter: string | null;
  searchTerm: string;
}

const NAMES_PER_PAGE = 12;

export default function NamesList({ gender, letter, searchTerm }: NamesListProps) {
  const [names, setNames] = useState<NameData[]>([]);
  const [loading, setLoading] = useState(false);
  const [visibleNames, setVisibleNames] = useState(NAMES_PER_PAGE);

  useEffect(() => {
    async function fetchNames() {
      if (gender && letter) {
        setLoading(true);
        try {
          const data = await loadNameData(gender, letter);
          setNames(data);
        } catch (error) {
          console.error('Error loading names:', error);
          setNames([]);
        }
        setLoading(false);
      }
    }
    fetchNames();
  }, [gender, letter]);

  const filteredNames = names.filter(name =>
    name.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const loadMore = () => {
    setVisibleNames(prev => prev + NAMES_PER_PAGE);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-8 h-8 border-2 border-purple-600 border-t-transparent rounded-full"
        />
      </div>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredNames.slice(0, visibleNames).map((name, index) => (
          <motion.div
            key={name.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-all"
          >
            <div className="flex justify-between items-start mb-2">
              <h3 className="text-xl font-semibold text-gray-900">
                {name.name}
              </h3>
              <div className="flex items-center gap-1 bg-purple-50 px-2 py-1 rounded-full">
                <Star size={14} className="text-purple-600" />
                <span className="text-sm font-medium text-purple-600">
                  {name.popularity}%
                </span>
              </div>
            </div>
            
            <p className="text-gray-600 text-sm mb-4">
              {name.nepali_meaning}
            </p>
            
            {name.personality && (
              <p className="text-purple-600 text-sm italic">
                {name.personality}
              </p>
            )}
          </motion.div>
        ))}
      </div>

      {visibleNames < filteredNames.length && (
        <div className="text-center mt-8">
          <button
            onClick={loadMore}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Load More Names
          </button>
        </div>
      )}
    </div>
  );
}